import { loadSession } from '../../auth/state.js'

function failure(message, status, code) {
  return Object.assign(new Error(message), { status, code })
}

// The caller owns the operation key: retries must not create a new payment action.
export async function requestCommerceManagement(path, { method = 'POST', body, idempotencyKey, signal } = {}) {
  let validPath = typeof path === 'string' && /^[A-Za-z0-9_-]+(?:\/[A-Za-z0-9._%~-]+)*$/.test(path)
  try {
    validPath = validPath && path.split('/').every((part) => {
      const decoded = decodeURIComponent(part)
      return !/[\\/\x00-\x1f\x7f]/.test(decoded) && !['.', '..'].includes(decoded)
    })
  } catch { validPath = false }
  if (!validPath) throw failure('Invalid Commerce management path', 400)
  method = method.toUpperCase()
  if (!['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) throw failure('Unsupported management method', 400)
  if (typeof idempotencyKey !== 'string' || !/^[A-Za-z0-9._:-]{1,200}$/.test(idempotencyKey)) {
    throw failure('A stable idempotency key is required', 400)
  }
  const token = loadSession()?.tokens?.accessToken
  if (!token) throw failure('Missing access token; sign in again', 401)
  let response
  try {
    response = await fetch(`/commerce-admin-api/${path}`, {
      method, signal, cache: 'no-store', redirect: 'error',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'Idempotency-Key': idempotencyKey },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause
    throw failure('Commerce request failed; outcome unknown. Retry the same action, not a new payment request.', 0, 'network_error')
  }
  if (response.status === 204 && response.ok) return null
  let payload
  try { payload = await response.json() } catch {
    throw failure(`Invalid Commerce JSON response (HTTP ${response.status}); verify the outcome before starting a new action`, response.status, 'invalid_response')
  }
  if (!response.ok || payload?.ok === false || (typeof payload?.code === 'number' && payload.code !== 0)) {
    const message = [payload?.message, payload?.error?.message, payload?.error, payload?.msg].find((value) => typeof value === 'string' && value)
    throw failure(message || `Commerce request failed (HTTP ${response.status})`, response.status, payload?.code)
  }
  if (!payload || typeof payload !== 'object') throw failure('Invalid Commerce response', response.status, 'invalid_response')
  return payload.data ?? payload
}
